/** Game calendar days follow Eastern Time — must match Rails `Game.today` / admin metrics. */
export const EASTERN_TIME_ZONE = 'America/New_York';

/** YYYY-MM-DD for the given instant in US Eastern (handles DST). */
export const getEasternDate = (when: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(when);

  const year = parts.find((p) => p.type === 'year')!.value;
  const month = parts.find((p) => p.type === 'month')!.value;
  const day = parts.find((p) => p.type === 'day')!.value;
  return `${year}-${month}-${day}`;
};

/** Today's puzzle date in Eastern Time. */
export const getTodaysDate = (): string => getEasternDate();

export const getGameNumber = (): number => {
  const start = Date.UTC(2024, 4, 27); // 2024-05-27, puzzle #1
  const [year, month, day] = getEasternDate().split('-').map(Number);
  const today = Date.UTC(year, month - 1, day);
  return Math.ceil((today - start) / 86400000) + 1;
};

export const formatTime = (elapsedTime: number): string => {
  if (!Number.isFinite(elapsedTime)) {
    return "0:00";
  }
  let minutes: number = Math.floor(elapsedTime / (1000 * 60));
  let seconds: number = Math.floor(elapsedTime / 1000 % 60);

  let secondsString: string = String(seconds).padStart(2, "0");

  return `${minutes}:${secondsString}`;
};

export const getUserId = (getStoredUserId: () => string | undefined, setStoredUserId: (id: string) => void): string => {
  const storedUserId = getStoredUserId();
  if (storedUserId === undefined) {
    const userId = crypto.randomUUID();
    setStoredUserId(userId);
    return userId;
  }
  return storedUserId;
};

export const getSolutionsFontColor = (numSolutions: number): string => {
  if (numSolutions === 1) return 'red';
  if (numSolutions <= 3) return 'yellow';
  return 'green';
};

/** Two slots per cardinal side — matches the char pairs used in solution validation. */
export type EdgeExtensionSlots = [boolean, boolean];

export type TileEdgeExtensions = {
  left: EdgeExtensionSlots;
  right: EdgeExtensionSlots;
  top: EdgeExtensionSlots;
  bottom: EdgeExtensionSlots;
};

const NO_EXTENSIONS: TileEdgeExtensions = {
  left: [false, false],
  right: [false, false],
  top: [false, false],
  bottom: [false, false],
};

/** Per-segment outward extensions for one tile when adjacent chars match. */
export const getTileEdgeExtensions = (
  tiles: string[],
  tileIndex: number
): TileEdgeExtensions => {
  if (!tiles[tileIndex]) return NO_EXTENSIONS;

  const extensions: TileEdgeExtensions = {
    left: [false, false],
    right: [false, false],
    top: [false, false],
    bottom: [false, false],
  };

  if (tileIndex % 3 > 0) {
    const neighbor = tiles[tileIndex - 1];
    const tile = tiles[tileIndex];
    if (neighbor) {
      extensions.left[0] = neighbor.charAt(3) === tile.charAt(0);
      extensions.left[1] = neighbor.charAt(4) === tile.charAt(7);
    }
  }

  if (tileIndex % 3 < 2) {
    const tile = tiles[tileIndex];
    const neighbor = tiles[tileIndex + 1];
    if (neighbor) {
      extensions.right[0] = tile.charAt(3) === neighbor.charAt(0);
      extensions.right[1] = tile.charAt(4) === neighbor.charAt(7);
    }
  }

  if (tileIndex > 2) {
    const neighbor = tiles[tileIndex - 3];
    const tile = tiles[tileIndex];
    if (neighbor) {
      extensions.top[0] = neighbor.charAt(5) === tile.charAt(2);
      extensions.top[1] = neighbor.charAt(6) === tile.charAt(1);
    }
  }

  if (tileIndex < 6) {
    const tile = tiles[tileIndex];
    const neighbor = tiles[tileIndex + 3];
    if (neighbor) {
      extensions.bottom[0] = tile.charAt(5) === neighbor.charAt(2);
      extensions.bottom[1] = tile.charAt(6) === neighbor.charAt(1);
    }
  }

  return extensions;
};

export const solutionValid = (tiles: string[]): boolean => {
  for (let i = 0; i < tiles.length; i++) {
    const tile = tiles[i];
    if (i % 3 > 0) {
      const leftTile = tiles[i - 1];
      if (
        leftTile.charAt(3) !== tile.charAt(0) ||
        leftTile.charAt(4) !== tile.charAt(7)
      ) {
        return false;
      }
    }
    if (i > 2) {
      const aboveTile = tiles[i - 3];
      if (
        aboveTile.charAt(5) !== tile.charAt(2) ||
        aboveTile.charAt(6) !== tile.charAt(1)
      ) {
        return false;
      }
    }
  }
  return true;
};
